import React from "react";
import styled from "styled-components";
import { useParams, useHistory } from "react-router-dom";
import routes from "routes";

const Page = styled.div`
    background: #f8fafc;
    min-height: 100vh;
    padding: 24px 32px;
`;

const Container = styled.div`
    max-width: 100%;
    margin: 0 auto;
`;

const BreadcrumbBar = styled.div`
    margin-bottom: 24px;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;

    &:hover {
        background: #ffffff;
        color: #0f172a;
    }
`;

const ClientHeader = styled.div`
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 32px;
    margin-bottom: 24px;
`;

const HeaderTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 32px;
    margin-bottom: 28px;

    @media (max-width: 968px) {
        flex-direction: column;
    }
`;

const ClientInfo = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
    flex: 1;
`;

const Avatar = styled.div`
    width: 72px;
    height: 72px;
    background: #3b82f6;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 700;
    font-size: 28px;
    flex-shrink: 0;
`;

const ClientDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const ClientName = styled.h1`
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
`;

const ClientMeta = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
`;

const MetaItem = styled.span`
    font-size: 14px;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 4px;

    strong {
        color: #475569;
        font-weight: 600;
    }
`;

const Divider = styled.span`
    color: #cbd5e1;
`;

const StatusBadge = styled.span`
    padding: 4px 10px;
    background: ${(p) => (p.active ? "#dcfce7" : "#fee2e2")};
    color: ${(p) => (p.active ? "#166534" : "#991b1b")};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const QuickActions = styled.div`
    display: flex;
    gap: 10px;

    @media (max-width: 968px) {
        width: 100%;
    }
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;
    white-space: nowrap;
    outline: none;

    ${(p) =>
            p.variant === "primary"
                    ? `
    background: #3b82f6;
    color: #ffffff;
    &:hover {
      background: #2563eb;
    }
  `
                    : `
    background: #f8fafc;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    &:hover {
      background: #ffffff;
      border-color: #cbd5e1;
    }
  `}

    &:focus {
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:active {
        transform: scale(0.98);
    }

    @media (max-width: 968px) {
        flex: 1;
    }
`;

const ContactInfo = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 24px;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const InfoLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
    font-size: 15px;
    color: #0f172a;
    font-weight: 500;
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 24px;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr;
    }
`;

const MainColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SideColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: 1200px) {
        order: -1;
    }
`;

const Card = styled.div`
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
`;

const CardHeader = styled.div`
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CardTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
`;

const CardCount = styled.span`
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
`;

const CardBody = styled.div`
    padding: 24px;
`;

const PoliciesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const PolicyCard = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    transition: all 0.2s ease;

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
`;

const PolicyHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
`;

const PolicyInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const PolicyType = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
`;

const PolicyNumber = styled.span`
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
`;

const PolicyStatusBadge = styled.span`
    padding: 4px 10px;
    background: ${(p) => (p.active ? "#dcfce7" : "#fee2e2")};
    color: ${(p) => (p.active ? "#166534" : "#991b1b")};
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    flex-shrink: 0;
`;

const PolicyDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
`;

const PolicyDetail = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    gap: 12px;
`;

const DetailLabel = styled.span`
    color: #64748b;
    font-weight: 500;
`;

const DetailValue = styled.span`
    color: #0f172a;
    font-weight: 600;
    text-align: right;
`;

const ViewPolicyButton = styled(Button)`
    width: 100%;
    margin-top: 4px;
`;

const ActionCard = styled(Card)``;

const ActionCardBody = styled(CardBody)`
    padding: 20px;
`;

const ActionSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:not(:last-child) {
        padding-bottom: 20px;
        margin-bottom: 20px;
        border-bottom: 1px solid #f1f5f9;
    }
`;

const ActionLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
`;

const ActionButton = styled.button`
    width: 100%;
    padding: 11px 18px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #0f172a;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #ffffff;
        border-color: #cbd5e1;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const PrimaryActionButton = styled(ActionButton)`
    background: #3b82f6;
    color: #ffffff;
    border: 1px solid #3b82f6;

    &:hover {
        background: #2563eb;
        border-color: #2563eb;
    }
`;

const ActionGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const StatsCard = styled(Card)``;

const StatsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const StatItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f1f5f9;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const StatLabel = styled.span`
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const ViewClient = () => {
    const { id } = useParams();
    const history = useHistory();

    const client = {
        id,
        name: id === "1" ? "Robert Kamau" : "Unknown Client",
        kra_pin: id === "1" ? "A123456789P" : "—",
        email: id === "1" ? "robert.kamau@example.com" : "—",
        phone: id === "1" ? "+254 712 345 678" : "—",
        status: id === "1" ? "Active" : "Dormant",
        joinDate: id === "1" ? "January 15, 2024" : "—",
    };

    const policies = [
        {
            id: 1,
            type: "Motor Insurance",
            policyNumber: "MOT-2024-001",
            status: "Active",
            premium: "KES 45,000",
            expiryDate: "Dec 31, 2024",
            vehicle: "Toyota Prado KDJ-150",
        },
        {
            id: 2,
            type: "Domestic Package",
            policyNumber: "DOM-2024-002",
            status: "Active",
            premium: "KES 25,000",
            expiryDate: "Nov 30, 2024",
            coverage: "Home & Contents",
        },
    ];

    const getInitials = (name) => {
        if (!name || name === "Unknown Client") return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <Page>
            <Container>
                <BreadcrumbBar>
                    <BackButton onClick={() => history.push(routes.clientManagement)}>
                        ← Back to Clients
                    </BackButton>
                </BreadcrumbBar>

                <ClientHeader>
                    <HeaderTop>
                        <ClientInfo>
                            <Avatar>{getInitials(client.name)}</Avatar>
                            <ClientDetails>
                                <ClientName>{client.name}</ClientName>
                                <ClientMeta>
                                    <MetaItem>
                                        <strong>Client ID:</strong> {client.id}
                                    </MetaItem>
                                    <Divider>•</Divider>
                                    <MetaItem>
                                        <strong>KRA PIN:</strong> {client.kra_pin}
                                    </MetaItem>
                                    <Divider>•</Divider>
                                    <StatusBadge active={client.status === "Active"}>
                                        {client.status}
                                    </StatusBadge>
                                </ClientMeta>
                            </ClientDetails>
                        </ClientInfo>

                        <QuickActions>
                            <Button variant="primary">Edit Client</Button>
                            <Button>Archive</Button>
                        </QuickActions>
                    </HeaderTop>

                    <ContactInfo>
                        <InfoItem>
                            <InfoLabel>Email Address</InfoLabel>
                            <InfoValue>{client.email}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Phone Number</InfoLabel>
                            <InfoValue>{client.phone}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Member Since</InfoLabel>
                            <InfoValue>{client.joinDate}</InfoValue>
                        </InfoItem>
                    </ContactInfo>
                </ClientHeader>

                <ContentGrid>
                    <MainColumn>
                        <Card>
                            <CardHeader>
                                <CardTitle>Policies</CardTitle>
                                <CardCount>{policies.length} Active</CardCount>
                            </CardHeader>
                            <CardBody>
                                <PoliciesGrid>
                                    {policies.map((policy) => (
                                        <PolicyCard key={policy.id}>
                                            <PolicyHeader>
                                                <PolicyInfo>
                                                    <PolicyType>{policy.type}</PolicyType>
                                                    <PolicyNumber>{policy.policyNumber}</PolicyNumber>
                                                </PolicyInfo>
                                                <PolicyStatusBadge active={policy.status === "Active"}>
                                                    {policy.status}
                                                </PolicyStatusBadge>
                                            </PolicyHeader>
                                            <PolicyDetails>
                                                <PolicyDetail>
                                                    <DetailLabel>Premium</DetailLabel>
                                                    <DetailValue>{policy.premium}</DetailValue>
                                                </PolicyDetail>
                                                <PolicyDetail>
                                                    <DetailLabel>Expiry Date</DetailLabel>
                                                    <DetailValue>{policy.expiryDate}</DetailValue>
                                                </PolicyDetail>
                                                {policy.vehicle && (
                                                    <PolicyDetail>
                                                        <DetailLabel>Vehicle</DetailLabel>
                                                        <DetailValue>{policy.vehicle}</DetailValue>
                                                    </PolicyDetail>
                                                )}
                                                {policy.coverage && (
                                                    <PolicyDetail>
                                                        <DetailLabel>Coverage</DetailLabel>
                                                        <DetailValue>{policy.coverage}</DetailValue>
                                                    </PolicyDetail>
                                                )}
                                            </PolicyDetails>
                                            <ViewPolicyButton>View Details</ViewPolicyButton>
                                        </PolicyCard>
                                    ))}
                                </PoliciesGrid>
                            </CardBody>
                        </Card>
                    </MainColumn>

                    <SideColumn>
                        <ActionCard>
                            <CardHeader>
                                <CardTitle>Quick Actions</CardTitle>
                            </CardHeader>
                            <ActionCardBody>
                                <ActionSection>
                                    <ActionLabel>Create</ActionLabel>
                                    <PrimaryActionButton>New Quotation</PrimaryActionButton>
                                </ActionSection>

                                <ActionSection>
                                    <ActionLabel>Communication</ActionLabel>
                                    <ActionGrid>
                                        <ActionButton>Send Email</ActionButton>
                                        <ActionButton>Send SMS</ActionButton>
                                    </ActionGrid>
                                </ActionSection>
                            </ActionCardBody>
                        </ActionCard>

                        <StatsCard>
                            <CardHeader>
                                <CardTitle>Statistics</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <StatsList>
                                    <StatItem>
                                        <StatLabel>Total Policies</StatLabel>
                                        <StatValue>{policies.length}</StatValue>
                                    </StatItem>
                                    <StatItem>
                                        <StatLabel>Active Policies</StatLabel>
                                        <StatValue>
                                            {policies.filter((p) => p.status === "Active").length}
                                        </StatValue>
                                    </StatItem>
                                    <StatItem>
                                        <StatLabel>Total Premium</StatLabel>
                                        <StatValue>KES 70,000</StatValue>
                                    </StatItem>
                                    <StatItem>
                                        <StatLabel>Last Activity</StatLabel>
                                        <StatValue>2 days ago</StatValue>
                                    </StatItem>
                                </StatsList>
                            </CardBody>
                        </StatsCard>
                    </SideColumn>
                </ContentGrid>
            </Container>
        </Page>
    );
};

export default ViewClient;